import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { notesApi, peopleApi } from "../api/client.js";
import { useBoard } from "../board/BoardContext.jsx";
import { useToast } from "../components/Toast.jsx";
import { useMailEnabled } from "../lib/useMailEnabled.js";
import { HEALTH, cadenceOf, daysSince, healthOf, ymd } from "../lib/domain.js";

/* Composing a note.
 *
 * Reached either from the nav (a free note) or from a Notify button, which
 * arrives with ?kind=task&playground=p03&index=2 and pre-writes a subject
 * and body specific to what is being chased. Sending a notify also records
 * when that item was last chased, so two people working the same queue do
 * not chase the same leader twice.
 *
 * Recipients come from the access list and nowhere else: the War Room
 * cannot be used to mail an arbitrary address. */
export function NotePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const mailEnabled = useMailEnabled();
  const { board, reload } = useBoard();

  const kind = params.get("kind") || "";
  const pgId = params.get("playground") || "";
  const index = Number(params.get("index"));

  const [people, setPeople] = useState([]);
  const [to, setTo] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState(null);

  const pg = board?.find((p) => p.id === pgId) || null;

  const prefill = useMemo(() => compose(kind, pg, index), [kind, pg, index]);

  useEffect(() => {
    let live = true;
    peopleApi
      .list()
      .then(({ users }) => live && setPeople(users.filter((u) => u.active && u.notes !== false)))
      .catch((e) => live && setProblem(e.message));
    return () => {
      live = false;
    };
  }, []);

  /* Fill the draft once the prefill is known, without overwriting anything
     already typed. */
  useEffect(() => {
    if (!prefill) return;
    setSubject((s) => s || prefill.subject);
    setBody((b) => b || prefill.body);
    if (prefill.suggestTo) {
      setTo((t) => (t.length ? t : prefill.suggestTo));
    }
  }, [prefill]);

  /* A Notify aimed at a person whose address is on the player record can
     suggest them; matching is by name, so it only suggests, never assumes. */
  useEffect(() => {
    if (!prefill?.assignee || !people.length || to.length) return;
    const match = findPerson(people, prefill.assignee);
    if (match) setTo([match.email]);
  }, [prefill, people, to.length]);

  async function send(e) {
    e.preventDefault();
    setProblem(null);
    setSending(true);
    try {
      if (kind && pg) {
        await notesApi.notify({
          kind,
          playgroundId: pg.id,
          index: Number.isFinite(index) ? index : undefined,
          to,
          subject,
          body,
        });
        /* The stamp the server just wrote belongs on the board. */
        reload();
      } else {
        await notesApi.send({ to, subject, body });
      }
      toast("Note sent to " + to.length + (to.length === 1 ? " person" : " people"));
      navigate(pg ? "/playground/" + pg.id : "/");
    } catch (err) {
      setProblem(err.message);
    } finally {
      setSending(false);
    }
  }

  const toggle = (email) =>
    setTo((prev) => (prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]));

  if (!mailEnabled) {
    return (
      <div className="page">
        <div className="pagehead">
          <div>
            <h2 className="pagetitle">Send a note</h2>
          </div>
          <Link className="btn btn-sm" to="/">
            Back to the board
          </Link>
        </div>
        <div className="notice">
          <b>Mail is not configured on this server.</b> Set SMTP_HOST and
          MAIL_FROM in the API’s environment and notes will send from here.
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="pagehead">
        <div>
          <h2 className="pagetitle">{prefill?.title || "Send a note"}</h2>
          {pg && (
            <p className="pagesub">
              About <Link to={"/playground/" + pg.id}>{pg.name}</Link>
            </p>
          )}
        </div>
        <Link className="btn btn-sm" to={pg ? "/playground/" + pg.id : "/"}>
          Cancel
        </Link>
      </div>

      {problem && (
        <p className="errline" role="alert">
          {problem}
        </p>
      )}

      <form onSubmit={send}>
        <div className="sechead">
          <span className="label">To · from the access list</span>
          <span className="rule" />
          <span className="acts">
            {to.length > 0 && <span className="pend">{to.length} selected</span>}
          </span>
        </div>

        {!people.length ? (
          <p className="empty">Nobody on the access list to write to.</p>
        ) : (
          <div className="chips">
            {people.map((p) => (
              <button
                key={p.email}
                type="button"
                className={"pickchip" + (to.includes(p.email) ? " on" : "")}
                aria-pressed={to.includes(p.email)}
                onClick={() => toggle(p.email)}
              >
                <b>{p.name || p.email}</b>
                {p.title?.trim() && <span className="chiprole">{p.title.trim()}</span>}
                <span className="mono">{p.email}</span>
              </button>
            ))}
          </div>
        )}

        <div className="field" style={{ marginTop: "16px" }}>
          <label className="label" htmlFor="note-subject">
            Subject
          </label>
          <input
            className="in"
            id="note-subject"
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </div>

        <div className="field" style={{ marginTop: "10px" }}>
          <label className="label" htmlFor="note-body">
            Note
          </label>
          <textarea
            className="in tall"
            id="note-body"
            required
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </div>

        <div className="formfoot">
          <span className="fromnote">
            Sent from the War Room’s mailbox, with your address as Reply-To.
          </span>
          <button
            className="btn btn-solid"
            type="submit"
            disabled={sending || !to.length}
          >
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* What a Notify is chasing, written out. Each kind names the item, the
   playground and the date that has passed, so the recipient does not have
   to open the board to know what is being asked. */
function compose(kind, pg, index) {
  if (!kind || !pg) return null;

  if (kind === "task") {
    const t = pg.tasks?.[index];
    if (!t) return null;
    return {
      title: "Notify about a task",
      assignee: t.assignee,
      subject: "[War Room] " + pg.name + " — " + (t.title || "task") + (t.due ? " (due " + t.due + ")" : ""),
      body:
        (t.assignee ? t.assignee + "," : "Hello,") +
        "\n\nThis task on " + pg.name + " is open:" +
        "\n\n  " + (t.title || "(untitled)") +
        "\n  status: " + t.status +
        (t.due ? "\n  committed date: " + t.due : "") +
        (t.critical ? "\n  flagged as a critical action" : "") +
        "\n\nCould you update the board, or tell us what it needs?" +
        "\n\nThanks.",
    };
  }

  if (kind === "kpi") {
    const k = pg.kpis?.[index];
    if (!k) return null;
    return {
      title: "Notify about a KPI review",
      subject: "[War Room] " + pg.name + " — " + (k.name || "KPI") + " is up for review",
      body:
        "Hello,\n\nThis KPI on " + pg.name + " is up for review:" +
        "\n\n  " + (k.name || "(unnamed)") +
        "\n  reading: " + (k.value || "—") + (k.target ? " of " + k.target : "") + (k.unit ? " " + k.unit : "") +
        (k.reviewOn ? "\n  review date: " + k.reviewOn : "") +
        (k.lastReviewedAt ? "\n  last reviewed: " + k.lastReviewedAt : "\n  never reviewed") +
        "\n\nCould we get a current reading?\n\nThanks.",
    };
  }

  if (kind === "review") {
    const h = healthOf(pg);
    const c = cadenceOf(pg);
    const days = pg.lastMeetingAt ? daysSince(pg.lastMeetingAt) : null;
    return {
      title: "Propose a review session",
      subject: "[War Room] " + pg.name + " — " + c.label.toLowerCase() + " review is due",
      body:
        (pg.owner ? pg.owner + "," : "Hello,") +
        "\n\n" + pg.name + " reads " + HEALTH[h].label.toLowerCase() + " today" +
        (days === null
          ? ", with no review ever logged."
          : ": " + days + " days since the last review, on a " + c.label.toLowerCase() + " cadence (at risk at " + c.atRisk + " days).") +
        "\n\nCan we put a session in this week?" +
        "\n\nAsked from the board on " + ymd(new Date()) + ".",
    };
  }

  return null;
}

/* An assignee is a name typed into a task; an address lives on the access
   list. Match exactly first, then on first name. */
function findPerson(people, name) {
  const n = String(name || "").trim().toLowerCase();
  if (!n) return null;
  return (
    people.find((p) => (p.name || "").trim().toLowerCase() === n) ||
    people.find((p) => {
      const first = (p.name || "").trim().toLowerCase().split(/\s+/)[0];
      return first === n || (p.name || "").trim().toLowerCase().startsWith(n);
    }) ||
    null
  );
}
